// src/components/sampleorders/EditKit.js

import React, { useEffect, useMemo, useState } from "react";
import { Box, Package, Plus, Trash2, Save, X, ImageIcon } from "lucide-react";

const EditKit = ({ kit, inventory, saving, canEdit = true, onSave, onCancel, onDelete }) => {
  const [localKit, setLocalKit] = useState(() => ({
    id: kit?.id || null,
    name: kit?.name || "",
    description: kit?.description || "",
    items:
      (kit?.items && Array.isArray(kit.items) && kit.items.length > 0
        ? kit.items
        : [{ productId: "", quantity: 1 }]) || [],
    isActive: kit?.isActive !== false,
  }));

  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setLocalKit({
      id: kit?.id || null,
      name: kit?.name || "",
      description: kit?.description || "",
      items:
        (kit?.items && Array.isArray(kit.items) && kit.items.length > 0
          ? kit.items
          : [{ productId: "", quantity: 1 }]) || [],
      isActive: kit?.isActive !== false,
    });
  }, [kit]);

  const items = localKit.items || [];

  const handleFieldChange = (field, value) => {
    setLocalKit((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    const current = updated[index] || { productId: "", quantity: 1 };
    updated[index] = {
      ...current,
      [field]:
        field === "quantity" ? (value === "" ? "" : Number(value) || 1) : value,
    };
    setLocalKit((prev) => ({ ...prev, items: updated }));
  };

  const handleAddItem = () => {
    setLocalKit((prev) => ({
      ...prev,
      items: [...(prev.items || []), { productId: "", quantity: 1 }],
    }));
  };

  const handleRemoveItem = (index) => {
    const currentItems = localKit.items || [];
    if (currentItems.length === 1) {
      setLocalKit((prev) => ({
        ...prev,
        items: [{ productId: "", quantity: 1 }],
      }));
      return;
    }
    const updated = currentItems.filter((_, i) => i !== index);
    setLocalKit((prev) => ({ ...prev, items: updated }));
  };

  const validItemCount = (localKit.items || []).filter(
    (i) => i.productId && Number(i.quantity) > 0
  ).length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canEdit) return;
    onSave(localKit);
  };

  const handleDelete = async () => {
    if (!localKit.id || !canEdit) return;

    const kitName = localKit.name || "this kit";
    const ok = window.confirm(
      `Delete "${kitName}"?\n\nThis will permanently remove this kit. Sample orders that used this kit will not be affected.`
    );
    if (!ok) return;

    setDeleting(true);
    try {
      await onDelete(localKit.id);
    } catch (err) {
      console.error("Error deleting kit:", err);
      alert("Failed to delete kit. Please try again.");
      setDeleting(false);
    }
  };

  const isNew = !localKit.id;

  const totalItems = validItemCount;
  const totalQuantity = (localKit.items || []).reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );

  const invById = useMemo(() => {
    const map = new Map();
    (inventory || []).forEach((i) => {
      if (i?.id) map.set(i.id, i);
    });
    return map;
  }, [inventory]);

  if (!canEdit) {
    return (
      <div className="overflow-hidden rounded-[28px] border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-rose-50 shadow-sm">
        <div className="border-b border-amber-100 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.22),_transparent_45%),linear-gradient(135deg,#fff7ed,#ffffff_45%,#fef2f2)] px-6 py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700">
                <Box className="h-3.5 w-3.5" />
                Default Kit
              </div>
              <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                {localKit.name || "Untitled kit"}
              </h2>
              {localKit.description ? (
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                  {localKit.description}
                </p>
              ) : null}
            </div>

            <button
              type="button"
              onClick={onCancel}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/80 text-slate-500 transition hover:bg-white"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 text-xs">
            <div className="rounded-full border border-amber-200 bg-white px-3 py-1.5 font-medium text-slate-700">
              {totalItems} item{totalItems === 1 ? "" : "s"}
            </div>
            <div className="rounded-full border border-amber-200 bg-white px-3 py-1.5 font-medium text-slate-700">
              Total quantity {totalQuantity}
            </div>
            <div className="rounded-full border border-amber-200 bg-white px-3 py-1.5 font-medium text-slate-700">
              Read-only menu view
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="grid gap-3 md:grid-cols-2">
            {items.map((item, index) => {
              const selectedProduct = item?.productId ? invById.get(item.productId) : null;
              const name = selectedProduct?.name || "Sample item";
              const imageUrl = selectedProduct?.imageUrl || "";
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 rounded-2xl border border-white/80 bg-white/90 px-4 py-3 shadow-sm"
                >
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                    {imageUrl ? (
                      <img src={imageUrl} alt={name} className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-900">{name}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {selectedProduct?.sku ? `SKU ${selectedProduct.sku}` : "Menu item"}
                    </div>
                  </div>
                  <div className="rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-800">
                    x{Number(item.quantity) || 1}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex justify-end">
            <div className="text-xs font-medium text-slate-500">
              Default kits created by admins are view only here.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white border border-slate-200 rounded-xl shadow-sm h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2.5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
              <Box className="w-4 h-4 text-white" />
            </div>

            <div className="min-w-0">
              <h2 className="text-base font-bold text-white leading-tight truncate">
                {isNew ? "New Kit" : "Edit Kit"}
              </h2>
              <p className="text-[11px] text-blue-100 leading-snug">
                {isNew
                  ? "Create a reusable sample kit"
                  : "Modify kit details and items"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 rounded-lg hover:bg-white/20 backdrop-blur-sm text-white flex items-center justify-center transition-all flex-shrink-0"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ✅ Compact stats + Active toggle (saves vertical space) */}
        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {validItemCount > 0 && (
              <>
                <div className="px-2 py-1 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-semibold flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" />
                  {totalItems} {totalItems === 1 ? "item" : "items"}
                </div>
                <div className="px-2.5 py-1 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-semibold">
                  Total Qty: {totalQuantity}
                </div>
              </>
            )}
          </div>

          <label className="inline-flex items-center gap-2 text-xs text-white/95 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={localKit.isActive}
              disabled={!canEdit}
              onChange={(e) => handleFieldChange("isActive", e.target.checked)}
              className="w-4 h-4 rounded border border-white/50 bg-white/10 text-white focus:ring-2 focus:ring-white/60"
            />
            <span className="font-semibold">
              Active kit{" "}
              <span className="font-normal text-white/70 hidden sm:inline">
                (available to users)
              </span>
            </span>
          </label>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex-1 flex flex-col overflow-hidden"
      >
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {!canEdit ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Default kits created by admins are read-only for standard users. You can still use them when building sample orders.
            </div>
          ) : null}

          {/* Basic Info */}
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-700 mb-1.5">
                Kit Name *
              </label>
              <input
                type="text"
                className="w-full h-10 border border-slate-300 rounded-xl px-3 text-[13px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all"
                value={localKit.name}
                disabled={!canEdit}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                placeholder="e.g., Starter Sushi Kit, Chef's Favorites..."
                required
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-700 mb-1.5">
                Description{" "}
                <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <textarea
                className="w-full border border-slate-300 rounded-xl px-3 py-2 text-[13px] resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                rows="3"
                value={localKit.description}
                disabled={!canEdit}
                onChange={(e) => handleFieldChange("description", e.target.value)}
                placeholder="Brief description of this kit..."
              />
            </div>
          </div>

          {/* Items Section */}
          <div className="border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-600" />
                <span className="text-[14px] font-bold text-slate-900">
                  Kit Items
                </span>
              </div>

              {canEdit ? (
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Item
                </button>
              ) : null}
            </div>

            {/* ✅ Single-line rows */}
            <div className="space-y-1.5">
              {items.map((item, index) => {
                const selectedProduct = item?.productId
                  ? invById.get(item.productId)
                  : null;

                return (
                  <div key={index} className="rounded-lg bg-white/90">
                    <div className="flex items-center gap-2.5 px-1 py-1.5">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center flex-shrink-0">
                        {selectedProduct?.imageUrl ? (
                          <img
                            src={selectedProduct.imageUrl}
                            alt={selectedProduct.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="w-5 h-5 text-slate-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <select
                          className="w-full h-10 border border-slate-300 rounded-lg px-3 text-[13px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          value={item.productId}
                          disabled={!canEdit}
                          onChange={(e) =>
                            handleItemChange(index, "productId", e.target.value)
                          }
                          required={index === 0}
                        >
                          <option value="">Select product</option>
                          {inventory.map((inv) => (
                            <option key={inv.id} value={inv.id}>
                              {inv.name} {inv.sku ? `(${inv.sku})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="w-16 flex-shrink-0">
                        <input
                          type="number"
                          min="1"
                          className="w-full h-10 border border-slate-300 rounded-lg px-2 text-[13px] text-center font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={item.quantity}
                          disabled={!canEdit}
                          onChange={(e) =>
                            handleItemChange(index, "quantity", e.target.value)
                          }
                          required={index === 0}
                          aria-label="Quantity"
                        />
                      </div>

                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all flex-shrink-0"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-gradient-to-br from-slate-50 to-white px-4 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-all"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>

              {!isNew && onDelete && canEdit ? (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl border border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-300 border-t-red-700 rounded-full animate-spin"></div>
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Kit
                    </>
                  )}
                </button>
              ) : null}
            </div>

            {canEdit ? (
              <button
                type="submit"
                disabled={saving || !localKit.name || validItemCount === 0}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-extrabold rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {isNew ? "Create Kit" : "Save Changes"}
                  </>
                )}
              </button>
            ) : (
              <div className="text-xs font-medium text-slate-500">
                Read-only default kit
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditKit;
